import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Eye, LogOut } from 'lucide-react';
import useAdmin from '../../hooks/useAdmin';
import AdminLogin from './AdminLogin';
import DynamicAdminPanel from './DynamicAdminPanel';

/**
 * ADMIN ACCESS COMPONENT - HIDDEN ROUTE HANDLER
 * 
 * Purpose: Secure admin access via hidden URL
 * - Direct access to admin panel without main site UI
 * - Authentication handling
 * - Clean session management
 * - Return to main site option
 */

const AdminAccess = () => {
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const { isAdmin, isLoading, loginAdmin, logoutAdmin } = useAdmin();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && isAdmin) {
      setShowAdminPanel(true);
    }
  }, [isAdmin, isLoading]);

  const handleAdminLogin = (success) => {
    if (success) {
      loginAdmin(true);
      setShowAdminPanel(true);
    }
  };

  const handleAdminLogout = () => {
    logoutAdmin();
    setShowAdminPanel(false);
  };

  const handleReturnToSite = () => {
    navigate('/');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading admin access...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-gray-800 rounded-2xl shadow-2xl p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Shield className="text-white" size={32} />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Admin Access</h2>
              <p className="text-gray-400">Secure portfolio management</p>
            </div>

            <AdminLogin onLogin={handleAdminLogin} />
            
            <div className="mt-6 pt-6 border-t border-gray-700">
              <button
                onClick={handleReturnToSite}
                className="w-full flex items-center justify-center space-x-2 text-gray-400 hover:text-white transition-colors py-2 rounded-xl hover:bg-gray-700"
              >
                <Eye size={16} />
                <span>Return to Portfolio</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showAdminPanel) {
    return (
      <DynamicAdminPanel 
        onClose={handleReturnToSite}
        onLogout={handleAdminLogout}
      />
    );
  }

  return null;
};

export default AdminAccess;